import React from "react";
import { Divider } from "@mui/material";
import colors from "common/styles/colors";
import { Typography } from "common/components/Typography";
import {
  ValuationZoneClosestCity,
  ValuationZoneFullCoeff,
} from "../../../types/api.types";
import { useQuery } from "react-query";
import { getValuationZoneClosestCityInfo } from "../../../api/geocode.api";
import { LoaderCentered } from "../../../components/loaders/LoaderCentered";
import { formatNumber } from "common/helpers/text.helpers";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import RemoveIcon from "@mui/icons-material/Remove";
import { useTranslation } from "react-i18next";
import { InfoRow } from "../../propertiesCRUD/InfoRow";

interface ValuationZoneCardProps {
  zoneInfo: ValuationZoneFullCoeff;
  coeffName?: string | null;
  distance: number | null;
  zoneCenter: { lat: number; lng: number };
}

export const ValuationZoneCard: React.FC<ValuationZoneCardProps> = ({
  zoneInfo,
  coeffName,
  distance,
  zoneCenter,
}) => {
  const { zone_nr } = zoneInfo;
  const coeff = zoneInfo.coeffs[coeffName ?? "flat_small"] ?? "-";
  const { t } = useTranslation();

  const { data: closestCityData, isLoading } =
    useQuery<ValuationZoneClosestCity>(
      ["closestCity", zoneCenter.lat, zoneCenter.lng],
      () => getValuationZoneClosestCityInfo(zoneCenter.lat, zoneCenter.lng),
      { enabled: !!zoneCenter }
    );

  const containerStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    borderRadius: "8px",
    padding: "20px",
    height: "100%",
    width: "320px",
    backgroundColor: colors.white,
    gap: "20px",
  };

  const sectionStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  };

  const trendRowStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "flex-end",
    fontStyle: "italic",
  };

  const trendStyle = (isPositive?: boolean): React.CSSProperties => ({
    display: "inline-flex",
    gap: "4px",
    color:
      isPositive === true
        ? colors.green
        : isPositive === false
        ? colors.red
        : colors.grey,
  });

  const renderTrend = (
    current: number,
    previous: number,
    unit = "",
    isIncreaseGood: boolean = true,
    formatTrend: boolean = false
  ): JSX.Element | null => {
    if (previous === undefined || previous === null) return null;

    const diff = current - previous;
    const percentage =
      previous !== 0 ? ((diff / previous) * 100).toFixed(1) : "0";
    const isPositive = isIncreaseGood ? diff > 0 : diff < 0;
    const isNeutral = diff === 0;
    const isIncreasing = diff > 0;

    const formattedDiff = `${diff > 0 ? "+" : ""}${
      formatTrend ? formatNumber(diff).toLocaleString() : diff.toLocaleString()
    }`;

    const formattedPercentage = `${diff > 0 ? "+" : ""}${percentage} %`;
    return (
      <span style={trendStyle(isPositive)}>
        {formattedDiff}{" "}
        {unit}
        {isIncreasing ? (
          <ArrowUpwardIcon style={{ fontSize: "1rem" }} />
        ) : isNeutral ? (
          <RemoveIcon style={{ fontSize: "1rem" }} />
        ) : (
          <ArrowDownwardIcon style={{ fontSize: "1rem" }} />
        )}
        ({formattedPercentage})
      </span>
    );
  };

  return (
    <div style={containerStyle}>
      <div style={sectionStyle}>
        <Typography variant="h3">
          {t("valuationZoneCard.valuationZone")} ({zone_nr})
        </Typography>
        <InfoRow
          label={`${t("valuationZoneCard.coeff")}:`}
          value={coeff}
          customLabelWidth="200px"
        />
        <InfoRow
          label={`${t("valuationZoneCard.distance")}:`}
          value={`${distance?.toFixed(2)} km`}
          customLabelWidth="200px"
        />
      </div>
      <Divider variant="fullWidth" />
      {!isLoading && closestCityData ? (
        <div style={sectionStyle}>
          <InfoRow
            label={`${t("valuationZoneCard.closestCity")}:`}
            value={closestCityData.city}
            customLabelWidth="300px"
          />
          <InfoRow
            label={`${t("valuationZoneCard.population")}:`}
            value={formatNumber(closestCityData.population)}
            customLabelWidth="300px"
            noWrap={true}
          />
          <div style={trendRowStyle}>
            <InfoRow
              label={`${t("valuationZoneCard.annualChange")}:`}
              value={renderTrend(
                closestCityData.population,
                closestCityData.previous_population,
                "",
                undefined,
                true
              )}
              customLabelWidth="100px"
            />
          </div>

          <InfoRow
            label={`${t("valuationZoneCard.employment")}:`}
            value={`${closestCityData.employment_rate} %`}
            customLabelWidth="300px"
            noWrap={true}
          />
          <div style={trendRowStyle}>
            <InfoRow
              label={`${t("valuationZoneCard.annualChange")}:`}
              value={renderTrend(
                closestCityData.employment_rate,
                closestCityData.previous_employment_rate,
                "%"
              )}
              customLabelWidth="100px"
            />
          </div>

          <InfoRow
            label={`${t("valuationZoneCard.averageSalary")}:`}
            value={`${formatNumber(closestCityData.average_salary)} €/${t("valuationZoneCard.month")}.`}
            customLabelWidth="300px"
            noWrap={true}
          />
          <div style={trendRowStyle}>
            <InfoRow
              label={`${t("valuationZoneCard.annualChange")}:`}
              value={renderTrend(
                closestCityData.average_salary,
                closestCityData.previous_average_salary,
                "€"
              )}
              customLabelWidth="100px"
            />
          </div>
        </div>
      ) : (
        <LoaderCentered />
      )}
    </div>
  );
};
